import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button, 
  Alert, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip, 
  Grid,
  Paper,
  Snackbar,
  LinearProgress,
  Fade,
  IconButton
} from '@mui/material';
import { 
  AccountCircle, 
  Subscriptions, 
  Star,
  Settings as SettingsIcon,
  ArrowBack,
  Email,
  Help,
  CalendarToday,
  ExitToApp,
  Share
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import FeedbackForm from './FeedbackForm';
import { subscriptionService, tokenService } from '../services/subscriptionService';

const Settings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [cancelAnswers, setCancelAnswers] = useState([]);

  const cancelQuestions = [
    {
      title: "What's the main reason you're considering canceling?",
      subtitle: "Help us understand what led to this decision",
      placeholder: "Please share your primary reason for leaving..."
    },
    {
      title: "How could we have better supported your wellness journey?",
      subtitle: "Your insights help us improve for future users",
      placeholder: "What would have made your experience better..."
    },
    {
      title: "Would you consider returning in the future?",
      subtitle: "Let us know what might bring you back",
      placeholder: "What changes or improvements would interest you..."
    }
  ];

  useEffect(() => {
    const checkAndFetch = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/auth');
          return;
        }

        setUser(user);
        setUserEmail(user.email);
        setLoading(false);

        // Fetch subscription status and token info using proper services
        await Promise.all([
          fetchSubscriptionStatus(),
          fetchTokenInfo()
        ]);
      } catch (error) {
        console.error('Error checking user:', error);
        setError('Failed to load user information');
        setLoading(false);
        setSubscriptionLoading(false);
      }
    };
    
    checkAndFetch();
  }, [navigate]);

  // Remove Crisp customer support chatbox if it exists
  useEffect(() => {
    // Remove any existing Crisp chat integration
    const removeCrispChat = () => {
      // Remove the script
      const crispScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (crispScript) {
        document.head.removeChild(crispScript);
      }
      
      // Clean up Crisp variables
      if (window.$crisp) {
        delete window.$crisp;
      }
      if (window.CRISP_WEBSITE_ID) {
        delete window.CRISP_WEBSITE_ID;
      }
      
      // Remove any Crisp-related DOM elements
      const crispElements = document.querySelectorAll('[id*="crisp"], [class*="crisp"]');
      crispElements.forEach(element => {
        element.remove();
      });
    };

    // Remove Crisp chat immediately when settings page loads
    removeCrispChat();

    // Also set up a cleanup function to ensure it stays removed
    return removeCrispChat;
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const data = await subscriptionService.getSubscriptionDetails();
      console.log('Settings Page - Subscription Data:', data);
      setSubscriptionStatus(data);
      
      // Also set token info from subscription data since it includes tokenBalance
      if (data.tokenBalance !== undefined) {
        setTokenInfo({ balance: data.tokenBalance });
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchTokenInfo = async () => {
    try {
      const data = await tokenService.getTokenInfo();
      console.log('Settings Page - Token Data:', data);
      setTokenInfo({ balance: data.tokenBalance || 0 });
    } catch (error) {
      console.error('Error fetching token info:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to logout');
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/subscription/update-payment-method`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        setError('Failed to update payment method');
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      setError('Failed to update payment method');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    setCancelStep(0);
    setCurrentAnswer('');
    setCancelAnswers([]);
  };

  const handleNextStep = () => {
    setCancelAnswers([...cancelAnswers, currentAnswer]);
    setCurrentAnswer('');
    setCancelStep(cancelStep + 1);
  };

  const handlePreviousStep = () => {
    if (cancelStep > 0) {
      const newAnswers = [...cancelAnswers];
      const previousAnswer = newAnswers.pop();
      setCancelAnswers(newAnswers);
      setCurrentAnswer(previousAnswer || '');
      setCancelStep(cancelStep - 1);
    }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      // Submit feedback
      if (cancelAnswers.length > 0 || currentAnswer.trim()) {
        const feedbackData = {
          type: 'cancellation',
          feedback: cancelAnswers.join('\n\n') + (currentAnswer.trim() ? '\n\n' + currentAnswer : ''),
          email: userEmail
        };

        await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/feedback/submit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedbackData),
        });
      }

      // Cancel subscription
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSuccessMessage('Subscription canceled successfully. You still have access until the end of your billing period.');
        setCancelDialogOpen(false);
        await fetchSubscriptionStatus(user.id);
      } else {
        setError('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSuccessMessage('Subscription reactivated successfully!');
        await fetchSubscriptionStatus(user.id);
      } else {
        setError('Failed to reactivate subscription');
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      setError('Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const formatSubscriptionStatus = (status) => {
    console.log('formatSubscriptionStatus called with:', status);
    if (!status) return 'Free Plan';
    
    // Check the actual subscriptionStatus from backend
    if (status.subscriptionStatus === 'active') return 'Active';
    if (status.subscriptionStatus === 'canceled') return 'Canceled';
    if (status.subscriptionStatus === 'past_due') return 'Past Due';
    if (status.subscriptionStatus === 'paused') return 'Paused';
    
    return 'Free Plan';
  };

  const formatPlanType = (status) => {
    console.log('formatPlanType called with:', status);
    if (!status || status.subscriptionStatus !== 'active') return 'Free Plan';
    
    // Check the actual subscriptionType from backend
    if (status.subscriptionType === 'yearly' || status.subscriptionType === 'premium_yearly') return 'Yearly';
    if (status.subscriptionType === 'monthly' || status.subscriptionType === 'premium_monthly') return 'Monthly';
    
    // If we have Stripe subscription data, try to determine from there
    if (status.stripeSubscription) {
      // Monthly subscriptions typically have shorter periods
      const periodEnd = new Date(status.stripeSubscription.currentPeriodEnd);
      const now = new Date();
      const diffInDays = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
      
      // If period is around 30 days, it's likely monthly; if around 365, it's yearly
      if (diffInDays > 300) return 'Yearly';
      if (diffInDays > 20 && diffInDays < 40) return 'Monthly';
    }
    
    // Fallback - if subscription_type is just 'premium', default to Monthly
    // (Most users would start with monthly plans)
    return 'Monthly';
  };

  const getStatusColor = (status) => {
    console.log('getStatusColor called with:', status);
    if (!status || status.subscriptionStatus !== 'active') return 'default';
    if (status.subscriptionStatus === 'active') return 'success';
    if (status.subscriptionStatus === 'canceled') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* Clean Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e4e7',
        py: { xs: 2, sm: 3 }
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between"
               flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}
                 flexDirection={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', sm: 'left' }}>
              <IconButton 
                onClick={() => navigate('/conversation')}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <ArrowBack sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
              
              <Box>
                <Typography variant="h4" fontWeight="600" color="#1a1a1a"
                           fontSize={{ xs: '1.5rem', sm: '2rem', md: '2.125rem' }}>
                  Settings
                </Typography>
                <Typography variant="body1" color="#6b7280" sx={{ mt: 0.5 }}
                           fontSize={{ xs: '0.875rem', sm: '1rem' }}>
                  Manage your account and subscription preferences
                </Typography>
              </Box>
            </Box>

            {/* Right Section - Token Display */}
            <Box display="flex" alignItems="center" flexShrink={0}>
              <Chip
                icon={<Star sx={{ color: '#ffd700', fontSize: { xs: 16, sm: 18, md: 20 } }} />}
                label={`${tokenInfo?.balance || 0} tokens`}
                onClick={() => navigate('/get-tokens')}
                sx={{ 
                  bgcolor: 'rgba(255, 215, 0, 0.1)',
                  color: '#b8860b',
                  fontWeight: 700,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.9rem' },
                  height: { xs: 32, sm: 36, md: 40 },
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255, 215, 0, 0.2)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                  },
                  transition: 'all 0.2s ease',
                  '& .MuiChip-label': {
                    px: { xs: 0.5, sm: 0.75 }
                  }
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
            
            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: { xs: 2, sm: 3 }, 
                  borderRadius: { xs: '8px', sm: '12px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* Success Message */}
            {successMessage && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: { xs: 2, sm: 3 }, 
                  borderRadius: { xs: '8px', sm: '12px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
                onClose={() => setSuccessMessage('')}
              >
                {successMessage}
              </Alert>
            )}

            {/* Loading State */}
            {subscriptionLoading && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: { xs: 6, sm: 8 },
                bgcolor: 'white',
                borderRadius: { xs: '12px', sm: '16px' },
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                mx: { xs: 1, sm: 0 }
              }}>
                <CircularProgress sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    ml: 2, 
                    color: '#374151',
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                  }}
                >
                  Loading your account details...
                </Typography>
              </Box>
            )}

            {/* Account Information Section */}
            {!subscriptionLoading && (
              <>
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                  <Grid item xs={12} lg={8}>
                    <Card sx={{ 
                      height: '100%',
                      bgcolor: 'white',
                      borderRadius: { xs: '12px', sm: '16px' },
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e5e7eb'
                    }}>
                      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: { xs: 3, sm: 4 },
                          flexWrap: 'wrap',
                          gap: 1
                        }}>
                          <AccountCircle sx={{ 
                            mr: { xs: 1.5, sm: 2 }, 
                            color: '#667eea', 
                            fontSize: { xs: 28, sm: 32 }
                          }} />
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#1f2937',
                              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' }
                            }}
                          >
                            Account Information
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                          <Grid item xs={12}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: { xs: 2.5, sm: 3 },
                              bgcolor: '#f8fafc',
                              borderRadius: { xs: '8px', sm: '12px' },
                              border: '1px solid #e5e7eb'
                            }}>
                              <Email sx={{ 
                                mr: { xs: 1.5, sm: 2 }, 
                                fontSize: { xs: 20, sm: 24 }, 
                                color: '#667eea',
                                flexShrink: 0
                              }} />
                              <Box minWidth="0" flex="1">
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280', 
                                    fontWeight: 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Email Address
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#1f2937',
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {loading ? 'Loading...' : userEmail || 'Not available'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: { xs: 2.5, sm: 3 },
                              bgcolor: '#f8fafc',
                              borderRadius: { xs: '8px', sm: '12px' },
                              border: '1px solid #e5e7eb',
                              height: '100%'
                            }}>
                              <Subscriptions sx={{ 
                                mr: { xs: 1.5, sm: 2 }, 
                                color: '#667eea',
                                fontSize: { xs: 20, sm: 24 },
                                flexShrink: 0
                              }} />
                              <Box minWidth="0" flex="1">
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280', 
                                    fontWeight: 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Subscription Status
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={subscriptionLoading ? 'Loading...' : formatSubscriptionStatus(subscriptionStatus)}
                                    color={getStatusColor(subscriptionStatus)}
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                      height: { xs: 24, sm: 28 },
                                      '& .MuiChip-label': {
                                        px: { xs: 1, sm: 1.5 }
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: { xs: 2.5, sm: 3 },
                              bgcolor: '#f8fafc',
                              borderRadius: { xs: '8px', sm: '12px' },
                              border: '1px solid #e5e7eb',
                              height: '100%'
                            }}>
                              <Help sx={{ 
                                mr: { xs: 1.5, sm: 2 }, 
                                color: '#667eea',
                                fontSize: { xs: 20, sm: 24 },
                                flexShrink: 0
                              }} />
                              <Box minWidth="0" flex="1">
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280', 
                                    fontWeight: 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Plan Type
                                </Typography>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    mt: 0.5, 
                                    color: '#1f2937',
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                  }}
                                >
                                  {subscriptionLoading ? 'Loading...' : formatPlanType(subscriptionStatus)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Subscription Details */}
                          {subscriptionStatus?.subscriptionStatus === 'active' && (
                            <Grid item xs={12}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: { xs: 2.5, sm: 3 },
                                bgcolor: '#f0f9ff',
                                borderRadius: { xs: '8px', sm: '12px' },
                                border: '1px solid #e0f2fe'
                              }}>
                                <CalendarToday sx={{ 
                                  mr: { xs: 1.5, sm: 2 }, 
                                  fontSize: { xs: 20, sm: 24 }, 
                                  color: '#0284c7',
                                  flexShrink: 0
                                }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#6b7280', 
                                      fontWeight: 500,
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                  >
                                    Next Renewal
                                  </Typography>
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: 600, 
                                      color: '#1f2937',
                                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {subscriptionStatus?.subscriptionEndsAt ? new Date(subscriptionStatus.subscriptionEndsAt).toLocaleDateString() : 'N/A'}
                                  </Typography>
                                  {subscriptionStatus?.cancelAtPeriodEnd && (
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: '#dc2626', 
                                        mt: 0.5, 
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                      }}
                                    >
                                      Will cancel at period end
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                          )}
                          
                          {/* Token Information */}
                          <Grid item xs={12}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: { xs: 2.5, sm: 3 },
                              bgcolor: '#f0fdf4',
                              borderRadius: { xs: '8px', sm: '12px' },
                              border: '1px solid #dcfce7'
                            }}>
                              <Star sx={{ 
                                mr: { xs: 1.5, sm: 2 }, 
                                fontSize: { xs: 20, sm: 24 }, 
                                color: '#16a34a',
                                flexShrink: 0
                              }} />
                              <Box sx={{ flex: 1, minWidth: 0, mr: { xs: 1.5, sm: 2 } }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280', 
                                    fontWeight: 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Token Balance
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#1f2937',
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' }
                                  }}
                                >
                                  {subscriptionLoading ? 'Loading...' : `${tokenInfo?.balance || 0} tokens`}
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                onClick={() => navigate('/get-tokens')}
                                size={window.innerWidth < 600 ? 'small' : 'medium'}
                                sx={{ 
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  px: { xs: 1.5, sm: 2 },
                                  py: { xs: 0.5, sm: 0.75 },
                                  minWidth: { xs: 'auto', sm: '120px' },
                                  flexShrink: 0,
                                  '&:hover': {
                                    borderColor: '#15803d',
                                    backgroundColor: 'rgba(22, 163, 74, 0.04)'
                                  }
                                }}
                              >
                                Add Tokens
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} lg={4}>
                    <Card sx={{ 
                      height: '100%',
                      bgcolor: 'white',
                      borderRadius: { xs: '12px', sm: '16px' },
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e5e7eb'
                    }}>
                      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: { xs: 2.5, sm: 3 }, 
                            color: '#1f2937',
                            fontSize: { xs: '1.125rem', sm: '1.25rem' }
                          }}
                        >
                          Quick Actions
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: { xs: 1.5, sm: 2 } 
                        }}>
                          {/* Subscription Management */}
                          {subscriptionStatus?.subscriptionStatus === 'active' && (
                            <>
                              <Button
                                variant="contained"
                                onClick={() => navigate('/referrals')}
                                fullWidth
                                startIcon={<Share sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                                sx={{ 
                                  bgcolor: '#10b981',
                                  py: { xs: 1.25, sm: 1.5 },
                                  borderRadius: { xs: '8px', sm: '12px' },
                                  fontWeight: 600,
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                  minHeight: { xs: 44, sm: 48 },
                                  '&:hover': {
                                    bgcolor: '#059669',
                                  }
                                }}
                              >
                                Refer Friends
                              </Button>
                              
                              <Button
                                variant="contained"
                                onClick={handleUpdatePaymentMethod}
                                disabled={actionLoading}
                                fullWidth
                                startIcon={actionLoading ? 
                                  <CircularProgress size={16} /> : 
                                  <Subscriptions sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                }
                                sx={{ 
                                  bgcolor: '#667eea',
                                  py: { xs: 1.25, sm: 1.5 },
                                  borderRadius: { xs: '8px', sm: '12px' },
                                  fontWeight: 600,
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                  minHeight: { xs: 44, sm: 48 },
                                  '&:hover': {
                                    bgcolor: '#5a67d8',
                                  }
                                }}
                              >
                                Update Payment
                              </Button>
                              
                              {subscriptionStatus.subscriptionStatus !== 'canceled' && !subscriptionStatus.stripeSubscription?.cancelAtPeriodEnd && (
                                <Button
                                  variant="outlined"
                                  onClick={handleOpenCancelDialog}
                                  disabled={actionLoading}
                                  fullWidth
                                  sx={{ 
                                    color: '#dc2626',
                                    borderColor: '#dc2626',
                                    py: { xs: 1.25, sm: 1.5 },
                                    borderRadius: { xs: '8px', sm: '12px' },
                                    fontWeight: 600,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    minHeight: { xs: 44, sm: 48 },
                                    '&:hover': {
                                      borderColor: '#b91c1c',
                                      backgroundColor: 'rgba(220, 38, 38, 0.04)'
                                    }
                                  }}
                                >
                                  Cancel Subscription
                                </Button>
                              )}
                              
                              {(subscriptionStatus.subscriptionStatus === 'canceled' || subscriptionStatus.stripeSubscription?.cancelAtPeriodEnd) && (
                                <Button
                                  variant="contained"
                                  onClick={handleReactivateSubscription}
                                  disabled={actionLoading}
                                  fullWidth
                                  startIcon={actionLoading ? 
                                    <CircularProgress size={16} /> : 
                                    <Subscriptions sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                  }
                                  sx={{ 
                                    bgcolor: '#16a34a',
                                    py: { xs: 1.25, sm: 1.5 },
                                    borderRadius: { xs: '8px', sm: '12px' },
                                    fontWeight: 600,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    minHeight: { xs: 44, sm: 48 },
                                    '&:hover': {
                                      bgcolor: '#15803d',
                                    }
                                  }}
                                >
                                  Reactivate
                                </Button>
                              )}
                            </>
                          )}
                          
                          {subscriptionStatus?.subscriptionStatus !== 'active' && (
                            <Button
                              variant="contained"
                              onClick={() => navigate('/subscribe')}
                              fullWidth
                              sx={{ 
                                bgcolor: '#16a34a',
                                py: { xs: 1.25, sm: 1.5 },
                                borderRadius: { xs: '8px', sm: '12px' },
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                minHeight: { xs: 44, sm: 48 },
                                '&:hover': {
                                  bgcolor: '#15803d',
                                }
                              }}
                            >
                              Subscribe Now
                            </Button>
                          )}
                          
                          <Button
                            variant="outlined"
                            startIcon={<Help sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            onClick={() => navigate('/faq')}
                            fullWidth
                            sx={{ 
                              borderColor: '#6b7280',
                              color: '#6b7280',
                              py: { xs: 1.25, sm: 1.5 },
                              borderRadius: { xs: '8px', sm: '12px' },
                              fontWeight: 600,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              minHeight: { xs: 44, sm: 48 },
                              '&:hover': {
                                borderColor: '#4b5563',
                                backgroundColor: 'rgba(107, 114, 128, 0.04)'
                              }
                            }}
                          >
                            FAQ & Support
                          </Button>
                          
                          <Button
                            variant="outlined"
                            startIcon={<SettingsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            onClick={() => navigate('/voice-settings')}
                            fullWidth
                            sx={{ 
                              borderColor: '#6b7280',
                              color: '#6b7280',
                              py: { xs: 1.25, sm: 1.5 },
                              borderRadius: { xs: '8px', sm: '12px' },
                              fontWeight: 600,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              minHeight: { xs: 44, sm: 48 },
                              '&:hover': {
                                borderColor: '#4b5563',
                                backgroundColor: 'rgba(107, 114, 128, 0.04)'
                              }
                            }}
                          >
                            Voice Settings
                          </Button>
                          
                          <Button
                            variant="outlined"
                            startIcon={<ExitToApp sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            onClick={handleLogout}
                            fullWidth
                            sx={{ 
                              borderColor: '#dc2626',
                              color: '#dc2626',
                              py: { xs: 1.25, sm: 1.5 },
                              borderRadius: { xs: '8px', sm: '12px' },
                              fontWeight: 600,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              minHeight: { xs: 44, sm: 48 },
                              '&:hover': {
                                borderColor: '#b91c1c',
                                backgroundColor: 'rgba(220, 38, 38, 0.04)'
                              }
                            }}
                          >
                            Logout
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Feedback & Support Section */}
                <Card sx={{ 
                  bgcolor: 'white',
                  borderRadius: { xs: '12px', sm: '16px' },
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  mt: { xs: 2, sm: 3 }
                }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '50%',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography sx={{ fontSize: '24px' }}>💭</Typography>
                      </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: '#1f2937',
                          fontSize: { xs: '1.125rem', sm: '1.25rem' }
                        }}
                      >
                        Feedback & Support
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 3,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        lineHeight: 1.6
                      }}
                    >
                      Help us improve your experience by sharing your thoughts, questions, or suggestions.
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => setFeedbackOpen(true)}
                      startIcon={<Typography>💬</Typography>}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 25,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Share Feedback
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}
        </Box>
      </Container>

      {/* Enhanced Cancellation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 4,
            minHeight: '500px'
          }
        }}
      >
        {cancelStep < cancelQuestions.length ? (
          // Funnel Questions
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Before you go...
                </Typography>
                <Chip 
                  label={`${cancelStep + 1} of ${cancelQuestions.length}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 600 }}
                />
              </Box>
              
              {/* Progress Bar */}
              <LinearProgress 
                variant="determinate" 
                value={((cancelStep + 1) / cancelQuestions.length) * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#f3f4f6',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#667eea',
                    borderRadius: 3
                  }
                }}
              />
            </DialogTitle>
            
            <DialogContent sx={{ py: 3 }}>
              <Fade key={cancelStep} in={true} timeout={300}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
                    {cancelQuestions[cancelStep].title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                    {cancelQuestions[cancelStep].subtitle}
                  </Typography>
                  
                  <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <textarea
                      value={currentAnswer}
                      onChange={(e) => setCurrentAnswer(e.target.value)}
                      placeholder={cancelQuestions[cancelStep].placeholder}
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        outline: 'none',
                        backgroundColor: 'white',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    
                    {currentAnswer.trim().length > 0 && (
                      <Typography variant="caption" sx={{ color: '#16a34a', mt: 1, display: 'block' }}>
                        ✓ Thank you for sharing your thoughts
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Fade>
            </DialogContent>
            
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={handlePreviousStep}
                disabled={cancelStep === 0}
                variant="outlined"
                sx={{ mr: 2 }}
              >
                Previous
              </Button>
              <Button 
                onClick={handleNextStep}
                disabled={!currentAnswer.trim()}
                variant="contained"
                sx={{ 
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#5a67d8' },
                  '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                }}
              >
                {cancelStep === cancelQuestions.length - 1 ? 'Continue' : 'Next'}
              </Button>
            </DialogActions>
          </>
        ) : (
          // Final Confirmation Step
          <>
            <DialogTitle>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626' }}>
                Cancel Subscription
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Thank you for sharing your feedback with us. We truly value your insights and will use them to improve our platform.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                Are you sure you want to cancel your subscription? You'll still have access until your current billing period ends.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can reactivate your subscription at any time from this settings page, and we'll be here to support your wellness journey whenever you're ready to return.
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Your feedback has been recorded and will help us serve future users better.
                </Typography>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setCancelDialogOpen(false)}>
                Keep Subscription
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={actionLoading}
                color="error"
                variant="contained"
                startIcon={actionLoading ? <CircularProgress size={16} /> : null}
              >
                {actionLoading ? 'Canceling...' : 'Cancel Subscription'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Feedback Form Dialog */}
      <FeedbackForm 
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        userEmail={userEmail}
      />
    </Box>
  );
};

export default Settings;
